import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";

const faqs: LearnFAQ[] = [
  {
    question: "Which is more effective, ketamine or SAINT®?",
    answer:
      "There is no head-to-head trial comparing IV ketamine and SAINT® directly, so neither can be called more effective. In the 2022 randomized trial, 79% of participants reached remission with SAINT® (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary. Ketamine studies report rapid reductions in depression scores, but those effects often fade without booster infusions. The right choice depends on your diagnosis, history, and preferences, and should be discussed with a psychiatrist.",
  },
  {
    question: "Can I do both SAINT® and ketamine?",
    answer:
      "Yes — Neuro Wellness offers both IV ketamine and SAINT® in the same clinic, so combining or sequencing them is possible under one psychiatric team. Some patients try one treatment first and, if they need more relief, consider the other. Whether combining is appropriate is a clinical decision that depends on your diagnosis, medication history, and contraindications. A free consultation is the best way to explore what fits your situation.",
  },
  {
    question: "Which lasts longer, SAINT® or ketamine?",
    answer:
      "SAINT® has published follow-up data: in the 2020 open-label study, most participants who reached remission maintained it at one year (Cole et al., Am J Psychiatry 2020;177(10):970–978). Individual results may vary. Ketamine's antidepressant effect often fades within days to weeks, which is why clinics typically schedule booster infusions. Durability varies from person to person for both treatments, and neither is a cure.",
  },
  {
    question: "Is ketamine FDA-approved for depression?",
    answer:
      "No. Ketamine is FDA-approved only as an anesthetic. Its use for depression by IV infusion is off-label. SPRAVATO® (esketamine), a related drug, is FDA-approved for treatment-resistant depression. SAINT® is FDA-cleared (K220177) for major depressive disorder in adults whose depression has not improved enough on prior antidepressant medication. Neuro Wellness treats adults 22 and older.",
  },
  {
    question: "Does insurance cover SAINT® or ketamine?",
    answer:
      "IV ketamine for depression is often not covered by insurance because it is off-label. SAINT® may be covered; coverage varies by plan. SPRAVATO® (esketamine) is more often covered for its FDA-approved indication. Neuro Wellness verifies your benefits for free, whatever carrier you have, so you know what your plan allows before you commit. Financing is available for SAINT® on a case-by-case basis.",
  },
  {
    question: "What are the side effects of SAINT® vs ketamine?",
    answer:
      "SAINT® side effects include headache, scalp discomfort, and facial twitching — usually mild and temporary. Ketamine side effects include dissociation, nausea, increased blood pressure, and sedation during infusion. Both have rare serious risks. TMS carries a small risk of seizure. Discuss these with your psychiatrist before deciding.",
  },
];

export const pageSaintVsKetamine = makeArticle({
  slug: "saint-vs-ketamine",
  title: "SAINT® TMS vs Ketamine for Depression",
  description:
    "SAINT® vs ketamine for depression: mechanism, FDA status, schedule, durability, cost, side effects, and whether you can combine them.",
  canonical: "/learn/saint-vs-ketamine",
  h1: "SAINT® TMS vs Ketamine for Depression",
  breadcrumbLabel: "SAINT® vs Ketamine",
  shortAnswer:
    "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) and ketamine are both treatments for treatment-resistant depression, but they work differently. SAINT® is a non-invasive brain stimulation device treatment that uses fMRI-guided magnetic pulses over 5 consecutive days. Ketamine is a medication — an anesthetic used off-label for depression by IV infusion. SAINT® is FDA-cleared for major depressive disorder; ketamine is FDA-approved only as an anesthetic. Neuro Wellness offers both in the same clinic. This page compares them by mechanism, administration, schedule, durability, cost, side effects, and whether you can combine them. The right choice depends on your diagnosis and should be discussed with a psychiatrist.",
  keyTakeaways: [
    "SAINT® is FDA-cleared (K220177) for major depressive disorder in adults whose depression has not improved enough on prior antidepressant medication; it is non-invasive and delivered over 5 consecutive days.",
    "Ketamine is FDA-approved only as an anesthetic; its use for depression by IV infusion is off-label. SPRAVATO® (esketamine) is the FDA-approved form for treatment-resistant depression.",
    "SAINT® uses fMRI-guided magnetic pulses; ketamine is an NMDA-receptor antagonist drug delivered by IV infusion.",
    "SAINT® at Neuro Wellness starts at $25,000; IV ketamine for depression is often not covered by insurance because it is off-label.",
    "Neuro Wellness offers both SAINT® and IV ketamine in the same clinic, so they can be combined or sequenced under one psychiatric team.",
  ],
  sections: [
    {
      heading:
        "Quick answer: SAINT® vs ketamine for treatment-resistant depression",
      paragraphs: [
        "If you are comparing SAINT® and ketamine, you are comparing two different treatments for treatment-resistant depression — depression that has not improved enough after trying antidepressant medication. They work through different mechanisms, have different regulatory statuses, and involve very different treatment experiences. Understanding those differences helps you make an informed choice.",
        "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is delivered on the Magnus Medical SAINT Neuromodulation System. It uses a functional MRI (fMRI) scan to personalize the treatment target for each person, then delivers 10 sessions per day over 5 consecutive days — about 50 sessions total. It is non-invasive: a coil placed on the scalp delivers magnetic pulses, with no needles, no drugs, and no anesthesia. It is FDA-cleared for major depressive disorder (MDD) in adults whose depression has not improved enough on prior antidepressant medication. Neuro Wellness treats adults 22 and older.",
        "Ketamine is a medication — an anesthetic that has been used for decades in operating rooms. Its use for depression by IV infusion is off-label, meaning the FDA has not approved ketamine for depression. Ketamine acts on the brain chemically through the bloodstream. A related drug, SPRAVATO® (esketamine), is FDA-approved for treatment-resistant depression. Neuro Wellness offers both SAINT® and IV ketamine in the same clinic.",
        "Both are legitimate treatment options. The question is which fits your diagnosis, medication history, and preferences. Learn more about [[SAINT®|/saint]] and take the [[free candidacy screener|/am-i-a-candidate-screener]] to see which may apply to you. For more on what treatment-resistant depression means, see our [[treatment-resistant depression guide|/learn/what-is-treatment-resistant-depression]].",
      ],
    },
    {
      heading: "Mechanism: how each treatment works",
      paragraphs: [
        "SAINT® uses intermittent theta burst stimulation (iTBS) — a pattern of magnetic pulses that induces electrical currents in the prefrontal cortex, the area of the brain involved in mood regulation. The fMRI scan is used to find the exact spot on your scalp that best connects to a mood-regulating brain circuit, so the stimulation is personalized to your brain. SAINT® is a device treatment; it introduces no chemical into the body.",
        "Ketamine is an NMDA-receptor antagonist — it blocks a receptor in the brain that responds to glutamate, a chemical involved in mood and learning. By affecting the glutamate system, ketamine is thought to promote changes in brain connections that may relieve depression. Ketamine is a drug that enters the bloodstream through an IV line and affects the brain pharmacologically.",
        "The fundamental difference: SAINT® is a non-invasive device that stimulates the brain magnetically; ketamine is a medication that alters brain chemistry through the bloodstream. Some patients prefer a non-drug approach; others may benefit from medication. Both are options for treatment-resistant depression.",
      ],
    },
    {
      heading: "Medication vs non-invasive brain stimulation",
      paragraphs: [
        "SAINT® is non-invasive — no needles, no drugs, no anesthesia. You sit awake in a chair while a coil on your scalp delivers magnetic pulses. Nothing enters your bloodstream. Ketamine is administered by IV infusion — a drug enters the bloodstream and affects the brain pharmacologically. You are monitored during and after the infusion because ketamine can cause dissociation and changes in blood pressure.",
        "For some people, a non-drug approach is preferable because it avoids the dissociative experience of ketamine and does not require an IV line. For others, a medication that works through a different chemical pathway than standard antidepressants may be the right fit. Neither is universally recommended — the choice depends on your history and should be discussed with a psychiatrist.",
      ],
    },
    {
      heading: "Administration and treatment schedule",
      paragraphs: [
        "SAINT® is delivered via a coil on the scalp while you sit awake in a chair. Each session lasts about 10 minutes, and you receive 10 sessions per day over 5 consecutive days — about 50 sessions total. You are in the clinic for most of the day during treatment week, but the full course is over in one week. SAINT® requires an fMRI scan beforehand so the target can be personalized.",
        "Ketamine for depression is administered by IV infusion over about 40 minutes per session. A typical induction series is six infusions over two to three weeks, with booster infusions afterward as needed. You are monitored during and after each infusion. You cannot drive yourself home after a ketamine infusion; you generally can drive after a SAINT® day.",
        "In short: SAINT® is a one-week intensive with full days at the clinic; ketamine is a multi-week series of shorter visits with ongoing boosters.",
      ],
    },
    {
      heading: "Patient experience and monitoring",
      paragraphs: [
        "During SAINT®, you feel a tapping or knocking sensation on the scalp and remain fully alert. There is no dissociation and no sedation. After each day, you return home. No post-session monitoring is required beyond the treatment day itself.",
        "During a ketamine infusion, you may experience dissociation — feeling detached from your body or surroundings — along with sedation and changes in perception. Blood pressure is monitored during and after the infusion. You must stay for monitoring after the infusion and cannot drive until the next day. Ketamine's experience is more pronounced than SAINT®'s scalp sensations.",
      ],
    },
    {
      heading: "Potential side effects",
      paragraphs: [
        "SAINT® side effects include headache, scalp discomfort, and facial twitching — usually mild and temporary. TMS carries a small risk of seizure. As with any depression treatment, there is a risk of worsening depression or suicidal thoughts.",
        "Ketamine side effects include dissociation, nausea, increased blood pressure, and sedation during infusion. In rare cases, perceptual disturbances can occur. Both treatments have rare serious risks, and both should be discussed with a psychiatrist before deciding.",
      ],
    },
    {
      heading: "Research context and evidence",
      paragraphs: [
        "SAINT® has published randomized controlled trial data. In the 2022 randomized trial, 79% of participants reached remission (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary. That figure comes from the SAINT® trial and applies to the SAINT® protocol on the Magnus system — it does not transfer to ketamine, standard TMS, or any other treatment. A 2020 open-label study and its one-year follow-up also reported high remission rates.",
        "Ketamine for depression has a larger body of research than SAINT®, including multiple randomized trials showing rapid reductions in depression scores. However, its use is off-label because ketamine is FDA-approved only as an anesthetic. SPRAVATO® (esketamine) is the FDA-approved form for treatment-resistant depression. Neither SAINT® nor IV ketamine has been compared head-to-head in a large trial, so neither can be called more effective than the other.",
        "For a deeper look at SAINT® remission and response data, see our [[SAINT® TMS success rates page|/learn/saint-tms-success-rates]].",
      ],
    },
    {
      heading: "Durability: how long each treatment lasts",
      paragraphs: [
        "Durability — how long the benefit lasts after the treatment course ends — is one of the most important differences between SAINT® and ketamine.",
        "SAINT® has published follow-up data. In the 2020 open-label study, most participants who reached remission maintained it at the one-year follow-up (Cole et al., Am J Psychiatry 2020;177(10):970–978). Individual results may vary. SAINT® is delivered as a discrete 5-day course; there is no routine schedule of booster sessions, though follow-up and maintenance are part of the care plan.",
        "Ketamine's antidepressant effect often fades within days to weeks after the infusion series ends. That is why clinics typically schedule booster infusions — sometimes weekly, monthly, or as needed — to maintain the benefit. Without boosters, the effect may not last. Durability varies from person to person, and ketamine is generally considered a treatment that requires ongoing maintenance infusions to sustain results.",
        "In short: SAINT® aims for a durable response from a single one-week course; ketamine typically requires a series of infusions followed by ongoing boosters to maintain any benefit. Neither is a cure, and individual results may vary.",
      ],
    },
    {
      heading: "Cost and insurance",
      paragraphs: [
        "SAINT® at Neuro Wellness starts at $25,000. That price reflects the fMRI scan, the FDA-cleared targeting software, the Magnus system, physician oversight, and the full 50-session week. SAINT® may be covered by insurance; coverage varies by plan. We verify your benefits for free, whatever carrier you have, so you know what your plan allows before you commit. Financing is available for SAINT® on a case-by-case basis and is discussed during your consultation.",
        "IV ketamine for depression is often not covered by insurance because it is off-label. Patients typically pay out of pocket for ketamine infusions. SPRAVATO® (esketamine), the FDA-approved form, is more often covered for its approved indication after prior authorization. Coverage varies by plan for all of these treatments.",
        "For the full SAINT® cost breakdown, see our [[SAINT® TMS cost page|/learn/saint-tms-cost]]. To check what your plan covers, start with a [[free benefits verification|/check-insurance-options]].",
      ],
    },
    {
      heading: "Can I combine SAINT® and ketamine?",
      paragraphs: [
        "Yes. Neuro Wellness offers both IV ketamine and SAINT® in the same clinic, so combining or sequencing them is possible under one psychiatric team. Some patients try one treatment first and, if they need more relief, consider the other. Because the two work through different mechanisms — magnetic stimulation vs a glutamate-targeting medication — they are not directly interchangeable, and one may be an option when the other has not provided enough relief.",
        "Whether combining is appropriate is a clinical decision that depends on your diagnosis, medication history, and contraindications. SAINT® is contraindicated for people with non-removable magnetic-sensitive metal near the head, certain implanted devices, or increased intracranial pressure. Ketamine has its own contraindications, including severe cardiovascular disease. A psychiatric evaluation determines which may be appropriate for you.",
        "A [[free consultation|/contactus]] is the best way to explore what fits your history. For more comparisons, see our guides to [[SAINT® vs SPRAVATO®|/learn/saint-vs-spravato]], [[SAINT® vs ECT|/learn/saint-vs-ect]], and [[what to do if SAINT® doesn't work|/learn/what-if-saint-doesnt-work]].",
      ],
    },
    {
      heading: "SAINT® vs ketamine: side-by-side comparison",
      paragraphs: [
        "The table below summarizes the key differences between SAINT® and IV ketamine for depression at a glance.",
      ],
      table: {
        headers: ["", "SAINT® (Magnus)", "IV Ketamine (off-label)"],
        rows: [
          [
            "FDA status",
            "FDA-cleared system (K220177) for MDD in adults with inadequate response to prior antidepressants; Breakthrough Device designation",
            "FDA-approved only as an anesthetic; off-label for depression; SPRAVATO® (esketamine) is the FDA-approved form for TRD",
          ],
          [
            "Sessions",
            "10 sessions/day × 5 days; ~50 sessions total",
            "~6 infusions over 2–3 weeks, then booster infusions as needed",
          ],
          [
            "Time to response",
            "Response typically measured within the 5-day week",
            "Rapid reductions in depression scores, often within hours to days of an infusion",
          ],
          [
            "Durability",
            "Published 1-year follow-up data (Cole et al., 2020); no routine booster schedule",
            "Effect often fades within days to weeks; ongoing booster infusions typically needed",
          ],
          [
            "Side effects",
            "Headache, scalp discomfort, facial twitching; small risk of seizure",
            "Dissociation, nausea, increased blood pressure, sedation; cannot drive after infusion",
          ],
          [
            "Cost / insurance",
            "Starts at $25,000 at NWCOA; may be covered, varies by plan; financing available",
            "Often not covered (off-label); typically self-pay; SPRAVATO® more often covered",
          ],
        ],
      },
    },
    {
      heading: "Who each treatment fits",
      paragraphs: [
        "SAINT® fits adults with major depressive disorder whose depression has not improved enough on prior antidepressant medication and who can commit to 5 consecutive days in the clinic. The compressed schedule suits people who want to avoid weeks of visits and who want a protocol with fMRI-guided personalized targeting and published randomized trial data. It is not for people with non-removable magnetic-sensitive metal near the head, certain implanted devices, or increased intracranial pressure.",
        "Ketamine may fit adults with treatment-resistant depression who want a medication that works through a different chemical pathway than standard antidepressants. Because ketamine is off-label for depression, anyone considering it should understand that it is not FDA-approved for that use. Ketamine has its own contraindications, including severe cardiovascular disease, and requires monitoring during and after each infusion.",
        "Neuro Wellness offers both SAINT® and IV ketamine, along with SPRAVATO® (esketamine), standard TMS, psychotherapy, and medication management. A [[free consultation|/contactus]] is the best way to learn which fits your diagnosis and circumstances. Call 954-827-9793 to speak with our team.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, Stimpson KH, Bentzley BS, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression. Am J Psychiatry. 2022;179(2):132–141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.21060580",
    },
    {
      citation:
        "Cole EJ, Phillips AL, Bentzley BS, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression: 1-year follow-up. Am J Psychiatry. 2020;177(10):970–978.",
      url: "https://doi.org/10.1176/appi.ajp.2020.20040470",
    },
    {
      citation:
        "U.S. Food and Drug Administration. Ketamine hydrochloride injection prescribing information.",
      url: "https://www.accessdata.fda.gov/drugsatfda_docs/label/2017/016812s063lbl.pdf",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification K220177 — Magnus Medical SAINT Neuromodulation System.",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm?ID=K220177",
    },
    {
      citation:
        "U.S. Food and Drug Administration. SPRAVATO® (esketamine) prescribing information.",
      url: "https://www.accessdata.fda.gov/drugsatfda_docs/label/2025/211243s019lbl.pdf",
    },
  ],
  about: [
    {
      "@type": "MedicalTherapy",
      name: "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT®)",
      description:
        "Accelerated, fMRI-guided intermittent theta burst stimulation delivered on the Magnus Neuromodulation System: 10 sessions per day for 5 consecutive days.",
    },
    {
      "@type": "Drug",
      name: "Ketamine",
      description:
        "NMDA-receptor antagonist anesthetic administered off-label by IV infusion for treatment-resistant depression; not FDA-approved for depression.",
    },
  ],
  relatedLinks: [
    {
      title: "SAINT® vs SPRAVATO®",
      href: "/learn/saint-vs-spravato",
      description: "How SAINT® compares to SPRAVATO® (esketamine)",
    },
    {
      title: "SAINT® vs ECT",
      href: "/learn/saint-vs-ect",
      description: "How SAINT® compares to electroconvulsive therapy",
    },
    {
      title: "What If SAINT® Doesn't Work?",
      href: "/learn/what-if-saint-doesnt-work",
      description: "Next steps if SAINT® does not provide enough relief",
    },
    {
      title: "SAINT® TMS Cost",
      href: "/learn/saint-tms-cost",
      description: "What SAINT® costs and how to plan for payment",
    },
    {
      title: "SAINT® Accelerated TMS",
      href: "/saint",
      description: "The 5-day, fMRI-guided protocol offered at our clinic",
    },
    {
      title: "Am I a Candidate?",
      href: "/am-i-a-candidate-screener",
      description: "Free candidacy screener",
    },
  ],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  toc: [
    {
      label: "Quick Answer",
      href: "#quick-answer-saint-vs-ketamine-for-treatment-resistant-depression",
    },
    { label: "Mechanism", href: "#mechanism-how-each-treatment-works" },
    {
      label: "Medication vs Brain Stimulation",
      href: "#medication-vs-non-invasive-brain-stimulation",
    },
    {
      label: "Administration & Schedule",
      href: "#administration-and-treatment-schedule",
    },
    {
      label: "Patient Experience & Monitoring",
      href: "#patient-experience-and-monitoring",
    },
    { label: "Side Effects", href: "#potential-side-effects" },
    { label: "Research & Evidence", href: "#research-context-and-evidence" },
    { label: "Durability", href: "#durability-how-long-each-treatment-lasts" },
    { label: "Cost & Insurance", href: "#cost-and-insurance" },
    {
      label: "Can I Combine Them?",
      href: "#can-i-combine-saint-and-ketamine",
    },
    {
      label: "Comparison Table",
      href: "#saint-vs-ketamine-side-by-side-comparison",
    },
    { label: "Who Each Fits", href: "#who-each-treatment-fits" },
    { label: "FAQ", href: "#faq" },
    { label: "Cited Research", href: "#cited-research" },
  ],
  safetyBlock:
    "SAINT® is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure and, as with any depression treatment, a risk of worsening depression or suicidal thoughts. If you are in crisis, call or text 988.",
  trademarkFooter:
    "SAINT® is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. NWCOA is not affiliated with Stanford University.",
  ctaBand: {
    headline: "See if SAINT® is a fit, free",
    primaryText: "Learn If It's a Fit",
    primaryHref: "/am-i-a-candidate-screener",
    secondaryText: "Check my insurance",
    secondaryHref: "/check-insurance-options",
    phone: "954-827-9793",
  },
});
